import { useCallback } from 'react';
import { MessageType } from '../../types/message';
import { ChatType } from '../../api/chats';
import { SpotifyTrack } from '../../types/message';
import { PlayableResult } from '../../components/playables-catalog';

interface UseHandleSendProps {
  chatObj: ChatType | null;
  messages: MessageType[];
  updateMessages: (newMessages: MessageType[] | ((prevMessages: MessageType[]) => MessageType[])) => void;
  setMessage: (message: string) => void;
  message: string;
  setIsPendingAiResponse: (isPending: boolean) => void;
  activePlayable: PlayableResult | null;
  currentMember: {
    member_type?: string;
    username?: string;
    display_name?: string;
    icon?: string;
  } | null;
  isPreview: boolean;
  setShowLoginModal: (show: boolean) => void;
  getMessagePersona: () => string | undefined;
  handleGenerateResponse: (finalChat: ChatType) => void;
}

interface HandleSendExtras {
  spotifyTrack?: SpotifyTrack;
}

export const useHandleSend = ({
  chatObj,
  messages,
  updateMessages,
  setMessage,
  message,
  setIsPendingAiResponse,
  activePlayable,
  currentMember,
  isPreview,
  setShowLoginModal,
  getMessagePersona,
  handleGenerateResponse
}: UseHandleSendProps) => {
  
  const handleSend = useCallback(async (e: React.FormEvent, extras: HandleSendExtras = {}) => {
    e.preventDefault();

    if (isPreview) {
      setShowLoginModal(true);
      return;
    }

    if (!chatObj) {
      console.error("Can't send message: no chat object");
      return;
    }

    try {
      if (extras.spotifyTrack) {
        const track = extras.spotifyTrack;
        setMessage('');
        
        const updatedChat = await chatObj.shareSpotifyTrack(
          track.id,
          track.name,
          track.artists,
          getMessagePersona()
        );
        updateMessages(updatedChat.messages || []);
        
        setIsPendingAiResponse(true);
        
        // The type casting here is a bit hacky, but it's how the original code did it
        const finalChat = await updatedChat.generate(activePlayable?.id as any);
        updateMessages(finalChat.messages || []);
        handleGenerateResponse(finalChat);
        setIsPendingAiResponse(false);
      } else {
        // Use the message from props rather than trying to read from the form
        const userMessage = message;
        setMessage('');
        
        // Create a temporary message with a unique ID
        const tempMessageId = `temp-${Date.now()}`;
        const tempMessage: MessageType = {
          id: tempMessageId,
          member_type: currentMember?.member_type || 'user',
          icon: currentMember?.icon,
          display_name: currentMember?.display_name,
          username: currentMember?.username,
          content: userMessage,
          content_type: 'message',
          isPending: true // Flag to indicate this is a temporary message
        };
        
        // Immediately update UI with the temporary message
        updateMessages([...messages, tempMessage]);
        
        // Then send the message to the backend
        const updatedChat = await chatObj.addMessage(userMessage, getMessagePersona());
        
        // Replace the temporary message with the real one from the backend
        const realMessages = updatedChat.messages?.map((msg: any) => {
          // Remove the isPending flag from any messages
          return { ...msg, isPending: false };
        }) || [];
        updateMessages(realMessages);
        
        setIsPendingAiResponse(true);

        // The type casting here is a bit hacky, but it's how the original code did it
        const finalChat = await updatedChat.generate(activePlayable?.id as any);
        updateMessages(finalChat.messages || []);
        handleGenerateResponse(finalChat);
        setIsPendingAiResponse(false);
      }
    } catch (err) {
      console.error('Failed to send/generate message:', err);
      setIsPendingAiResponse(false);
      
      // If there was an error, mark the temporary message as failed
      updateMessages((prevMessages: MessageType[]) => 
        prevMessages.map((msg: MessageType) => 
          msg.isPending ? { ...msg, isPending: false, sendFailed: true } : msg
        )
      );
    }
  }, [
    chatObj,
    messages,
    message,
    updateMessages,
    setMessage,
    setIsPendingAiResponse,
    activePlayable,
    currentMember,
    isPreview,
    setShowLoginModal,
    getMessagePersona,
    handleGenerateResponse
  ]);

  return { handleSend };
};

export default useHandleSend;
